// Chronological list of rounds inside the combat modal. Each line shows
// the attacker's damage and the defender's post-strike morale so the
// player can read the fight's momentum at a glance.
//
// Spec: docs/02-systems/combat-visualisation.md §3a (rounds list).

import { type MessageKey, useT } from '../../i18n/index.js';
import { rich } from '../../i18n/rich.js';
import type { CombatRound } from '../../render/combatRounds.js';

interface CombatRoundListProps {
  readonly rounds: ReadonlyArray<CombatRound>;
}

function unitLabel(id: string): string {
  return `#${id.replace(/^unit_/, '')}`;
}

function moraleTone(level: number): string {
  if (level >= 3) return 'text-emerald-700 dark:text-emerald-300';
  if (level >= 2) return 'text-amber-700 dark:text-amber-300';
  if (level >= 1) return 'text-orange-700 dark:text-orange-400';
  return 'text-rose-700 dark:text-rose-400';
}

function moraleKey(level: number): MessageKey {
  if (level >= 3) return 'combat.morale.high';
  if (level >= 2) return 'combat.morale.medium';
  if (level >= 1) return 'combat.morale.low';
  return 'combat.morale.critical';
}

export function CombatRoundList({ rounds }: CombatRoundListProps) {
  const t = useT();
  if (rounds.length === 0) {
    return <p className="text-xs italic text-muted-foreground">{t('combat.noRounds')}</p>;
  }
  return (
    <ol className="flex flex-col gap-1.5 text-xs">
      {rounds.map((r) => (
        <li
          key={`${r.tick}-${r.attackerId}-${r.defenderId}`}
          className="flex items-baseline justify-between gap-3 rounded-md border border-border/40 bg-background/30 px-2 py-1"
        >
          <span className="text-muted-foreground">{`T+${r.tick}`}</span>
          <span className="flex-1 truncate text-muted-foreground">
            {rich(t('combat.strike'), {
              attacker: (
                <span className="font-medium text-foreground">{unitLabel(r.attackerId)}</span>
              ),
              defender: (
                <span className="font-medium text-foreground">{unitLabel(r.defenderId)}</span>
              ),
              damage: (
                <span className="font-semibold text-rose-700 dark:text-rose-300">
                  {r.attackerDamage}
                </span>
              ),
            })}
          </span>
          <span className={`whitespace-nowrap ${moraleTone(r.defenderMoraleAfter)}`}>
            {rich(t('combat.morale'), {
              unit: unitLabel(r.defenderId),
              level: <span className="font-semibold">{t(moraleKey(r.defenderMoraleAfter))}</span>,
            })}
            {r.defenderKilled ? t('combat.kill') : ''}
          </span>
        </li>
      ))}
    </ol>
  );
}
